import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDaysYMD, parseYMD, toYMD } from '../lib/utils';
import { useSearchParams } from 'react-router-dom';
import {
  driversApi, driverDeductionsApi, driverAddonsApi,
  staffApi, ownerOperatorsApi, dispatchersApi,
  paystubOverridesApi, paystubEmailsApi, companyApi,
} from '../lib/api';
import { PaystubLoadsTable } from '../components/PaystubLoadsTable';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Receipt, ChevronLeft, ChevronRight, Download, Plus, Trash2, DollarSign,
  Users, Truck, Briefcase, Headphones, FileText, Pencil, RotateCcw, AlertCircle,
  Send, Eye, EyeOff, Package,
} from 'lucide-react';
import { toast } from 'sonner';

// Map UI tab id to backend person_type for the paystub-overrides API
const TYPE_TO_PERSON_TYPE = {
  drivers: 'driver',
  staff: 'staff',
  dispatchers: 'dispatcher',
  owner_operators: 'owner_operator',
};

const PERSON_TYPES = [
  { id: 'all', label: 'All', icon: FileText },
  { id: 'drivers', label: 'Drivers', icon: Truck },
  { id: 'owner_operators', label: 'Owner-Ops', icon: Users },
  { id: 'staff', label: 'Staff', icon: Briefcase },
  { id: 'dispatchers', label: 'Dispatchers', icon: Headphones },
];

// Find the Friday of the pay-period week. Pay periods cover the previous
// Saturday → Friday (standard "pay on Friday" rule). Returns the Friday's ISO date.
const fridayOf = (anyDate) => {
  const d = parseYMD(anyDate);
  // Friday = day 5 (0=Sun, 1=Mon, ..., 5=Fri, 6=Sat); if it IS Friday, returns that day
  d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7));
  return toYMD(d);
};
// Monday-of-week containing `date` — used as the API anchor for loads/deductions
const mondayOf = (anyDate) => {
  const d = parseYMD(anyDate);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return toYMD(d);
};
// Saturday-before-Friday: pay week covers Sat..Fri (e.g. Fri Jun 12 → Sat Jun 6 .. Fri Jun 12)
const payWeekStart = (fridayIso) => addDaysYMD(fridayIso, -6);
const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

export default function PaystubsHub() {
  const today = new Date();
  const [activeType, setActiveType] = useState('all');
  const [payDayFri, setPayDayFri] = useState(fridayOf(today));
  // Pay week starts on the Monday of the SAME calendar week as the Friday pay day.
  // Older code went `mondayOf(payWeekStart(payDayFri))` which walked back an
  // extra Saturday and landed on the previous week's Monday — every bonus /
  // deduction / override created from this page ended up saved under the wrong
  // `week_start`. See iteration_63.json for the RCA.
  const weekStart = useMemo(() => mondayOf(payDayFri), [payDayFri]);
  // We use Monday-of-the-pay-week as the API "week_start" — matches loads/deductions data.
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // for "All" tab
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Add bonus/deduction dialog (drivers only for now — other person types add manually elsewhere)
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addMode, setAddMode] = useState('bonus'); // 'bonus' | 'deduction'
  const [addForm, setAddForm] = useState({ name: '', amount: '', notes: '' });

  // Edit Gross Pay (manual override) dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ amount: '', notes: '' });
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Inline PDF preview state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);

  // Ref + helper to auto-scroll the detail panel into view when a person is selected
  // on mobile. Without this the user clicks a name and the paystub appears below the
  // long list — easy to miss.
  const detailPanelRef = useRef(null);
  const scrollToDetail = () => {
    if (typeof window === 'undefined') return;
    // Only auto-scroll on small viewports (the lg layout puts list & detail side-by-side).
    if (window.innerWidth >= 1024) return;
    requestAnimationFrame(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  // Nudged whenever a bonus is added/deleted so BonusList refetches
  const [bonusRefreshKey, setBonusRefreshKey] = useState(0);

  // Email Paystub state
  const [emailingPaystub, setEmailingPaystub] = useState(false);

  // Company-wide: show each load's gross + rate on driver paystubs (screen, PDF, email)
  const [showLoadGross, setShowLoadGross] = useState(false);
  useEffect(() => {
    companyApi.getPaystubSettings()
      .then((r) => setShowLoadGross(!!r.data?.show_load_gross_on_paystubs))
      .catch(() => {});
  }, []);
  const handleToggleLoadGross = async (checked) => {
    setShowLoadGross(checked);
    try {
      await companyApi.updatePaystubSettings({ show_load_gross_on_paystubs: checked });
      toast.success(checked ? 'Load gross now shown on driver paystubs' : 'Load gross hidden on driver paystubs');
      loadSummary();
    } catch (e) {
      setShowLoadGross(!checked);
      toast.error(e.response?.data?.detail || 'Failed to update setting');
    }
  };

  // Deep link from Summary → Driver Paystubs: /paystubs?driver=<id>&week=<monday>
  const [searchParams] = useSearchParams();
  const deepLinkRef = useRef(searchParams.get('driver'));
  useEffect(() => {
    const wk = searchParams.get('week');
    if (wk) setPayDayFri(fridayOf(wk + 'T12:00:00'));
    if (deepLinkRef.current) setActiveType('drivers');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedId(null);
    setSelectedType(null);
    setSummary(null);
    setSearch('');
    loadPeople();
    if (deepLinkRef.current && activeType === 'drivers') {
      setSelectedId(deepLinkRef.current);
      setSelectedType('drivers');
      deepLinkRef.current = null;
      scrollToDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  useEffect(() => {
    const effectiveType = selectedType || activeType;
    if (selectedId && effectiveType !== 'all') loadSummary();
  }, [selectedId, weekStart, activeType, selectedType]);

  const loadPeople = async () => {
    setLoading(true);
    try {
      if (activeType === 'all') {
        // Load everyone — tag each row with its source type
        const [dr, oo, st, di] = await Promise.all([
          driversApi.getAll().then((r) => r.data).catch(() => []),
          ownerOperatorsApi.getAll().then((r) => r.data).catch(() => []),
          staffApi.getAll().then((r) => r.data).catch(() => []),
          dispatchersApi.getAll().then((r) => r.data).catch(() => []),
        ]);
        const merged = [
          ...dr.map((p) => ({ ...p, __type: 'drivers' })),
          ...oo.map((p) => ({ ...p, __type: 'owner_operators' })),
          ...st.map((p) => ({ ...p, __type: 'staff' })),
          ...di.map((p) => ({ ...p, __type: 'dispatchers' })),
        ];
        merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPeople(merged);
      } else {
        let res;
        if (activeType === 'drivers') res = await driversApi.getAll();
        else if (activeType === 'owner_operators') res = await ownerOperatorsApi.getAll();
        else if (activeType === 'staff') res = await staffApi.getAll();
        else res = await dispatchersApi.getAll();
        setPeople((res.data || []).map((p) => ({ ...p, __type: activeType })));
      }
    } catch { toast.error('Failed to load people'); }
    setLoading(false);
  };

  // Normalize summaries from 4 different endpoints into one shape
  const loadSummary = async () => {
    const effectiveType = selectedType || activeType;
    if (!selectedId || effectiveType === 'all') return;
    setSummaryLoading(true);
    try {
      let normalized = null;
      if (effectiveType === 'drivers') {
        const r = await driversApi.getPaySummary(selectedId, weekStart);
        normalized = { ...r.data, __kind: 'driver-full' };
      } else if (effectiveType === 'staff') {
        const r = await staffApi.getPaySummary(selectedId, weekStart);
        const d = r.data;
        normalized = {
          __kind: 'simple',
          person_name: d.staff?.name,
          subtitle: (d.staff?.role || '').replace(/_/g, ' '),
          gross_pay: d.gross_pay,
          net_pay: d.net_pay,
          load_count: d.loads_count,
          is_override: d.is_override,
          override_notes: d.override_notes,
        };
      } else if (effectiveType === 'dispatchers') {
        const r = await dispatchersApi.performance(selectedId, { week_start: weekStart });
        const d = r.data;
        normalized = {
          __kind: 'simple',
          person_name: d.name,
          subtitle: 'Dispatcher',
          gross_pay: d.totals?.gross_pay,
          net_pay: d.totals?.gross_pay,  // dispatchers have no per-week deductions in the perf endpoint
          load_count: d.totals?.load_count,
          is_override: d.is_override,
          override_notes: d.override_notes,
        };
      } else if (effectiveType === 'owner_operators') {
        const r = await ownerOperatorsApi.getSummary(selectedId, weekStart);
        const d = r.data;
        normalized = {
          __kind: 'simple',
          person_name: d.owner_operator_name,
          subtitle: 'Owner-Operator',
          gross_pay: d.weekly_totals?.total_gross,
          net_pay: d.weekly_totals?.net_payout,
          load_count: d.weekly_totals?.total_loads,
          is_override: d.is_override,
          override_notes: d.override_notes,
          override_label: 'Net Payout', // for OOs the override targets net
        };
      }
      setSummary(normalized);
    } catch { toast.error('Failed to load pay summary'); }
    setSummaryLoading(false);
  };

  // Shift pay date by 7 days
  const shiftWeek = (delta) => {
    setPayDayFri(addDaysYMD(payDayFri, delta * 7));
  };

  // Filtered list — search by name
  const filteredPeople = useMemo(() => {
    if (!search.trim()) return people;
    const s = search.toLowerCase();
    return people.filter((p) =>
      (p.name || '').toLowerCase().includes(s) ||
      (p.email || '').toLowerCase().includes(s) ||
      (p.phone || '').toLowerCase().includes(s),
    );
  }, [people, search]);

  const handleDownloadPdf = async () => {
    const person = (people.find((p) => p.id === selectedId)
      || { name: summary?.driver_name || summary?.person_name || 'paystub' });
    const effectiveType = selectedType || activeType;
    try {
      let blob;
      if (effectiveType === 'drivers') {
        const r = await driversApi.getPaystub(selectedId, weekStart);
        blob = r.data;
      } else if (effectiveType === 'staff') {
        const r = await staffApi.getPaystub(selectedId, weekStart);
        blob = r.data;
      } else if (effectiveType === 'dispatchers') {
        const r = await dispatchersApi.getPaystub(selectedId, weekStart);
        blob = r.data;
      } else if (effectiveType === 'owner_operators') {
        const r = await ownerOperatorsApi.exportPdf(selectedId, weekStart);
        blob = r.data;
      } else {
        toast.error('Paystub PDF not supported for this person type'); return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Paystub_${(person.name || 'person').replace(/\s+/g, '_')}_${weekStart}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Paystub downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to download'); }
  };

  const openAdd = (mode) => {
    setAddMode(mode);
    setAddForm({ name: '', amount: '', notes: '' });
    setAddDialogOpen(true);
  };
  const handleSaveAddOrDeduct = async () => {
    if (!addForm.name?.trim() || !addForm.amount) {
      toast.error('Name and amount are required'); return;
    }
    const amt = parseFloat(addForm.amount);
    if (!(amt > 0)) { toast.error('Amount must be greater than 0'); return; }
    try {
      const payload = {
        driver_id: selectedId,
        week_start: weekStart,
        name: addForm.name,
        amount: amt,
        notes: addForm.notes || '',
      };
      if (addMode === 'bonus') {
        payload.addon_type = 'bonus';
        await driverAddonsApi.create(payload);
        toast.success('Bonus added');
      } else {
        await driverDeductionsApi.create(payload);
        toast.success('Deduction added');
      }
      setAddDialogOpen(false);
      setBonusRefreshKey((k) => k + 1);
      loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteDeduction = async (id) => {
    if (!window.confirm('Delete this deduction?')) return;
    try { await driverDeductionsApi.delete(id); toast.success('Deleted'); loadSummary(); }
    catch { toast.error('Delete failed'); }
  };
  const handleDeleteAddon = async (id) => {
    if (!window.confirm('Delete this bonus?')) return;
    try { await driverAddonsApi.delete(id); toast.success('Deleted'); setBonusRefreshKey((k) => k + 1); loadSummary(); }
    catch { toast.error('Delete failed'); }
  };

  // Toggle include/exclude of a single load on this driver's paystub.
  // Optimistic UI: flip locally first, then persist; roll back on error.
  const togglePaystubLoad = async (loadId, excluded) => {
    setSummary((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      next.loads = (next.loads || []).map((l) =>
        l.id === loadId ? { ...l, excluded_from_paystub: excluded } : l);
      return next;
    });
    try {
      await driversApi.togglePaystubLoad(selectedId, loadId, excluded);
      // Refresh totals from server (gross / net now differ)
      loadSummary();
      toast.success(excluded ? 'Load excluded from paystub' : 'Load included in paystub');
    } catch (e) {
      // Roll back optimistic flip
      setSummary((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        next.loads = (next.loads || []).map((l) =>
          l.id === loadId ? { ...l, excluded_from_paystub: !excluded } : l);
        return next;
      });
      toast.error('Update failed');
    }
  };

  // ----- Manual gross-pay override (works for all 4 person types) -----
  const openOverrideDialog = () => {
    const currentGross =
      summary?.__kind === 'driver-full'
        ? summary?.gross_pay
        : summary?.__kind === 'simple' && summary?.override_label === 'Net Payout'
          ? summary?.net_pay
          : summary?.gross_pay;
    setOverrideForm({
      amount: summary?.is_override ? String(currentGross ?? '') : '',
      notes: summary?.override_notes || '',
    });
    setOverrideDialogOpen(true);
  };

  const handleSaveOverride = async () => {
    const amt = parseFloat(overrideForm.amount);
    if (!(amt >= 0)) { toast.error('Amount must be a positive number'); return; }
    const effectiveType = selectedType || activeType;
    const personType = TYPE_TO_PERSON_TYPE[effectiveType];
    if (!personType) return;
    setOverrideSaving(true);
    try {
      await paystubOverridesApi.upsert({
        person_type: personType,
        person_id: selectedId,
        week_start: weekStart,
        override_amount: amt,
        notes: overrideForm.notes || '',
      });
      toast.success('Paystub amount saved');
      setOverrideDialogOpen(false);
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save override');
    }
    setOverrideSaving(false);
  };

  const handleRevertOverride = async () => {
    if (!window.confirm('Remove the manual override and revert to the calculated amount?')) return;
    const effectiveType = selectedType || activeType;
    const personType = TYPE_TO_PERSON_TYPE[effectiveType];
    try {
      await paystubOverridesApi.delete(personType, selectedId, weekStart);
      toast.success('Reverted to calculated amount');
      loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to revert'); }
  };

  // -------- Inline PDF preview --------
  const fetchPdfBlob = async () => {
    const effectiveType = selectedType || activeType;
    if (!selectedId || effectiveType === 'all') return null;
    if (effectiveType === 'drivers')          return (await driversApi.getPaystub(selectedId, weekStart)).data;
    if (effectiveType === 'staff')            return (await staffApi.getPaystub(selectedId, weekStart)).data;
    if (effectiveType === 'dispatchers')      return (await dispatchersApi.getPaystub(selectedId, weekStart)).data;
    if (effectiveType === 'owner_operators')  return (await ownerOperatorsApi.exportPdf(selectedId, weekStart)).data;
    return null;
  };

  const loadPdfPreview = async () => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    if (!pdfPreviewVisible) return;
    setPdfPreviewLoading(true);
    try {
      const blob = await fetchPdfBlob();
      if (blob && blob.size > 0) {
        setPdfPreviewUrl(URL.createObjectURL(blob));
      } else if (blob) {
        toast.error('PDF preview is empty. Try regenerating.');
      }
    } catch (e) {
      // Surface the actual server error to the user instead of a generic message — helps debug
      // why the owner-operator preview occasionally returned 4xx/5xx in the past.
      // eslint-disable-next-line no-console
      console.error('PDF preview error:', e);
      const detail = e?.response?.data?.detail || e?.message || 'Failed to load PDF preview';
      toast.error(typeof detail === 'string' ? detail : 'Failed to load PDF preview');
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  useEffect(() => {
    loadPdfPreview();
    return () => { if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, weekStart, activeType, selectedType, pdfPreviewVisible, summary?.is_override, summary?.gross_pay, showLoadGross]);

  // -------- Email Paystub --------
  const handleEmailPaystub = async () => {
    const effectiveType = selectedType || activeType;
    const personType = TYPE_TO_PERSON_TYPE[effectiveType];
    if (!personType || !selectedId) return;
    setEmailingPaystub(true);
    try {
      const res = await paystubEmailsApi.send(personType, selectedId, weekStart);
      toast.success(`Paystub emailed to ${res.data?.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send paystub email');
    } finally {
      setEmailingPaystub(false);
    }
  };

  const selectedPerson = useMemo(
    () => filteredPeople.find((p) => p.id === selectedId) || people.find((p) => p.id === selectedId),
    [filteredPeople, people, selectedId],
  );
  const effectiveType = selectedType || activeType;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="paystubs-hub">
      <div>
        <h1 className="text-3xl font-manrope font-bold flex items-center gap-2">
          <Receipt className="h-7 w-7 text-blue-600" />Paystubs
        </h1>
        <p className="text-muted-foreground mt-1">
          Navigate any week, edit deductions and bonuses, then download the PDF — for Drivers, Owner-Operators, Staff, or Dispatchers.
        </p>
      </div>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          {PERSON_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} data-testid={`paystubs-tab-${t.id}`}>
                <Icon className="h-3.5 w-3.5 mr-1.5" />{t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {PERSON_TYPES.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              {/* Left: people list with search */}
              <Card className="lg:col-span-4">
                <CardHeader className="pb-2 space-y-2">
                  <CardTitle className="text-base">{t.label}</CardTitle>
                  <Input
                    placeholder={`Search ${t.label.toLowerCase()} by name…`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="paystubs-search"
                    className="h-8 text-sm"
                  />
                </CardHeader>
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  {loading ? <div className="p-4"><Skeleton className="h-12 mb-2" /><Skeleton className="h-12 mb-2" /></div> :
                  filteredPeople.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">
                    {people.length === 0 ? `No ${t.label.toLowerCase()} yet` : 'No matches for your search'}
                  </div> :
                  filteredPeople.map((p) => {
                    const typeBadge = {
                      drivers: { label: 'Driver', color: 'bg-green-100 text-green-700' },
                      owner_operators: { label: 'Owner-Op', color: 'bg-amber-100 text-amber-700' },
                      staff: { label: 'Staff', color: 'bg-cyan-100 text-cyan-700' },
                      dispatchers: { label: 'Dispatcher', color: 'bg-violet-100 text-violet-700' },
                    }[p.__type] || { label: '—', color: 'bg-slate-100 text-slate-700' };
                    return (
                      <button
                        key={`${p.__type}-${p.id}`}
                        onClick={() => { setSelectedId(p.id); setSelectedType(p.__type); scrollToDetail(); }}
                        className={`w-full text-left px-4 py-3 border-b hover:bg-muted/30 transition ${selectedId === p.id ? 'bg-blue-50 dark:bg-blue-950/30 border-l-4 border-l-blue-600' : ''}`}
                        data-testid={`paystubs-person-${p.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold truncate">{p.name}</div>
                          {p.status && p.status !== 'active' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase bg-slate-200 text-slate-600" data-testid={`paystubs-person-inactive-${p.id}`}>{p.status.replace('_', ' ')}</span>
                          )}
                          {activeType === 'all' && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase ${typeBadge.color}`}>{typeBadge.label}</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.email || p.phone || '—'}
                        </div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Right: paystub detail */}
              <div className="lg:col-span-8 space-y-4" ref={detailPanelRef}>
                {/* Week navigator — anchored on Friday pay day */}
                <Card>
                  <CardContent className="p-3 flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="paystubs-prev-week">
                      <ChevronLeft className="h-4 w-4 mr-1" />Prev
                    </Button>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Pay Day · Friday</div>
                      <div className="font-mono font-bold text-base">{fmt(payDayFri)}</div>
                      <div className="text-[10px] text-muted-foreground">Covers loads {fmt(weekStart).split(',')[1]}–{fmt(payDayFri).split(',')[1]}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="paystubs-next-week">
                      Next<ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>

                {!selectedId ? (
                  <Card><CardContent className="text-center py-16 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
                    <p>Select a person on the left to view their paystub</p>
                  </CardContent></Card>
                ) : summaryLoading || !summary ? (
                  <Skeleton className="h-64" />
                ) : effectiveType !== 'drivers' && summary.__kind === 'simple' ? (
                  // ---- Non-driver compact paystub card (staff / dispatcher / owner-op) ----
                  <Card>
                    <CardHeader className="pb-2 flex flex-row items-start justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {summary.person_name}
                          {summary.is_override && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]" data-testid="paystub-override-badge">
                              <AlertCircle className="h-3 w-3 mr-1" />OVERRIDE
                            </Badge>
                          )}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground capitalize">{summary.subtitle}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {summary.is_override && (
                          <Button size="sm" variant="ghost" onClick={handleRevertOverride} className="text-amber-700" data-testid="paystub-revert-override">
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />Revert
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={openOverrideDialog} data-testid="paystub-edit-gross">
                          <Pencil className="h-3.5 w-3.5 mr-1" />Edit {summary.override_label || 'Gross'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleEmailPaystub} disabled={emailingPaystub} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="paystub-email">
                          {emailingPaystub ? <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" /> : <><Send className="h-3.5 w-3.5 mr-1" />Email Paystub</>}
                        </Button>
                        <Button size="sm" onClick={handleDownloadPdf} className="bg-blue-600 hover:bg-blue-700" data-testid="paystubs-download-pdf">
                          <Download className="h-3.5 w-3.5 mr-1.5" />Download PDF
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3 text-center mb-3">
                        <div><div className="text-[10px] uppercase text-muted-foreground">Loads</div><div className="text-xl font-bold font-mono">{summary.load_count || 0}</div></div>
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">{summary.override_label || 'Gross'}</div>
                          <div className="text-xl font-bold font-mono text-emerald-600">${Number(summary.gross_pay || 0).toLocaleString()}</div>
                        </div>
                        <div><div className="text-[10px] uppercase text-muted-foreground">Net Pay</div><div className="text-xl font-bold font-mono text-blue-600">${Number(summary.net_pay || 0).toLocaleString()}</div></div>
                      </div>
                      {summary.is_override && summary.override_notes && (
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
                          <span className="font-semibold">Override note:</span> {summary.override_notes}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-muted-foreground">
                        Bonuses and deductions for this person are managed on the
                        {' '}
                        <button className="underline" onClick={() => {
                          const route = effectiveType === 'staff' ? '/staff' : effectiveType === 'owner_operators' ? '/owner-operators' : '/dispatchers';
                          window.location.href = route;
                        }}>{effectiveType.replace('_', '-')} page</button>.
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* No-unit banner — historic/inactive drivers still see the paystub */}
                    {!summary.has_unit && (
                      <Card className="border-amber-200 bg-amber-50/40" data-testid="paystub-no-unit-banner">
                        <CardContent className="p-3 text-xs text-amber-800 flex items-start gap-2">
                          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          <span>
                            <b>{summary.driver_name}</b> has no active unit assignment. Showing manual bonuses,
                            deductions, and overrides so historical paystubs stay accessible.
                          </span>
                        </CardContent>
                      </Card>
                    )}

                    {/* Summary stats */}
                    <Card>
                      <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          {summary.driver_name}{summary.has_unit ? ` — Unit ${summary.unit_number}` : ''}
                          {summary.is_override && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]" data-testid="paystub-override-badge">
                              <AlertCircle className="h-3 w-3 mr-1" />OVERRIDE
                            </Badge>
                          )}
                        </CardTitle>
                        <div className="flex gap-2 flex-wrap">
                          {summary.is_override && (
                            <Button size="sm" variant="ghost" onClick={handleRevertOverride} className="text-amber-700" data-testid="paystub-revert-override">
                              <RotateCcw className="h-3.5 w-3.5 mr-1" />Revert
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={openOverrideDialog} data-testid="paystub-edit-gross">
                            <Pencil className="h-3.5 w-3.5 mr-1" />Edit Gross
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleEmailPaystub} disabled={emailingPaystub} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="paystub-email">
                            {emailingPaystub ? <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" /> : <><Send className="h-3.5 w-3.5 mr-1" />Email Paystub</>}
                          </Button>
                          <Button size="sm" onClick={handleDownloadPdf} className="bg-blue-600 hover:bg-blue-700" data-testid="paystubs-download-pdf">
                            <Download className="h-3.5 w-3.5 mr-1.5" />Download PDF
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div><div className="text-[10px] uppercase text-muted-foreground">Loads</div><div className="text-xl font-bold font-mono">{summary.load_count}</div></div>
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Gross</div>
                            <div className="text-xl font-bold font-mono text-emerald-600" data-testid="paystub-gross">${summary.gross_pay.toLocaleString()}</div>
                            {summary.total_addons > 0 && (
                              <div className="text-[10px] text-emerald-700 font-medium" data-testid="paystub-gross-bonus-note">
                                incl. +${summary.total_addons.toLocaleString()} bonus
                              </div>
                            )}
                          </div>
                          <div><div className="text-[10px] uppercase text-muted-foreground">Deductions</div><div className="text-xl font-bold font-mono text-red-600" data-testid="paystub-deductions">{summary.total_deductions ? '-' : ''}${summary.total_deductions.toLocaleString()}</div></div>
                          <div><div className="text-[10px] uppercase text-muted-foreground">Net Pay</div><div className="text-xl font-bold font-mono text-blue-600" data-testid="paystub-net">${summary.net_pay.toLocaleString()}</div></div>
                        </div>
                        {summary.is_override && summary.override_notes && (
                          <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
                            <span className="font-semibold">Override note:</span> {summary.override_notes}
                          </div>
                        )}
                        {summary.has_unit && summary.pay_basis && (
                          <div className="mt-3 text-xs rounded px-3 py-2 bg-slate-50 dark:bg-slate-900/40 border flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="paystub-pay-basis">
                            <span className="font-semibold">Pay basis:</span>
                            <span>{summary.pay_basis}</span>
                            {!summary.pay_calc && ['percentage', 'percentage_gross'].includes(summary.pay_type) && (
                              <span className="font-mono text-muted-foreground" data-testid="paystub-pay-calc">
                                {summary.show_load_gross
                                  ? `= $${Number(summary.load_gross_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} × ${summary.pay_rate}% = $${Number(summary.calculated_gross_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                                  : `= $${Number(summary.calculated_gross_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} driver pay`}
                              </span>
                            )}
                            {summary.is_override && <span className="text-amber-700">(overridden manually)</span>}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Weekly Loads — per-load driver pay. Users can toggle any load's
                        inclusion; excluded loads drop out of gross/net here AND on the PDF. */}
                    <Card data-testid="paystub-weekly-loads-card">
                      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 flex-wrap">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Package className="h-4 w-4 text-blue-600" />
                          Loads · {summary.loads?.length || 0}
                          {summary.excluded_load_count > 0 && (
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                              {summary.excluded_load_count} excluded
                            </Badge>
                          )}
                        </CardTitle>
                        <label className="flex items-center gap-2 text-xs cursor-pointer select-none" title="Applies to the on-screen paystub, the PDF and the emailed paystub for all drivers">
                          <Switch
                            checked={showLoadGross}
                            onCheckedChange={handleToggleLoadGross}
                            data-testid="paystub-show-load-gross-toggle"
                          />
                          <span className="font-medium">Show load gross for all drivers</span>
                        </label>
                        {!showLoadGross && summary.driver_shows_load_amounts && (
                          <p className="w-full text-xs text-muted-foreground" data-testid="paystub-driver-amounts-on-note">
                            Load gross is on for this driver from their profile ("Show load amounts on paystub").
                          </p>
                        )}
                        {summary.pay_calc && (
                          <p className="w-full text-xs text-blue-700 dark:text-blue-300" data-testid="paystub-calc-note">
                            Pay for this week was set with Calculate pay ({summary.pay_calc.lines} loads). Manage it on Summary → Driver Paystubs.
                          </p>
                        )}
                      </CardHeader>
                      <CardContent className="p-0">
                        {(summary.loads || []).length === 0 ? (
                          <div className="text-center py-6 text-sm text-muted-foreground">No loads in this period</div>
                        ) : (
                          <PaystubLoadsTable
                            loads={summary.loads}
                            payType={summary.pay_type}
                            payRate={summary.pay_rate}
                            showGross={!!summary.show_load_gross}
                            onToggle={summary.pay_calc ? undefined : togglePaystubLoad}
                          />
                        )}
                      </CardContent>
                    </Card>

                    {/* Deductions */}
                    <Card>
                      <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4 text-red-600" />Deductions</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => openAdd('deduction')} className="border-red-300 text-red-700" data-testid="paystubs-add-deduction">
                          <Plus className="h-3 w-3 mr-1" />Add
                        </Button>
                      </CardHeader>
                      <CardContent className="p-0">
                        {(summary.deductions || []).length === 0 ? (
                          <div className="text-center py-6 text-sm text-muted-foreground">No deductions this week</div>
                        ) : (
                          <Table>
                            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
                            <TableBody>
                              {summary.deductions.map((d) => (
                                <TableRow key={d.id}>
                                  <TableCell className="font-medium">{d.name}</TableCell>
                                  <TableCell className="text-xs text-muted-foreground">{d.notes || '—'}</TableCell>
                                  <TableCell className="text-right font-mono text-red-600">-${d.amount.toLocaleString()}</TableCell>
                                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => handleDeleteDeduction(d.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>

                    {/* Bonuses */}
                    <Card>
                      <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4 text-emerald-600" />Bonuses / Add-ons</CardTitle>
                        <Button size="sm" variant="outline" onClick={() => openAdd('bonus')} className="border-emerald-300 text-emerald-700" data-testid="paystubs-add-bonus">
                          <Plus className="h-3 w-3 mr-1" />Add
                        </Button>
                      </CardHeader>
                      <CardContent className="p-0">
                        <BonusList driverId={selectedId} weekStart={weekStart} refreshKey={bonusRefreshKey} onDelete={handleDeleteAddon} />
                      </CardContent>
                    </Card>

                    {/* YTD */}
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Year-to-Date</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div><div className="text-[10px] uppercase text-muted-foreground">YTD Gross</div><div className="text-lg font-bold font-mono text-emerald-700">${summary.ytd.gross_pay.toLocaleString()}</div></div>
                          <div><div className="text-[10px] uppercase text-muted-foreground">YTD Deductions</div><div className="text-lg font-bold font-mono text-red-700">-${summary.ytd.total_deductions.toLocaleString()}</div></div>
                          <div><div className="text-[10px] uppercase text-muted-foreground">YTD Net</div><div className="text-lg font-bold font-mono text-blue-700">${summary.ytd.net_pay.toLocaleString()}</div></div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Inline PDF preview — available for ALL person types */}
                {selectedId && effectiveType !== 'all' && summary && (
                  <Card>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        Paystub PDF Preview
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPdfPreviewVisible((v) => !v)}
                        data-testid="paystub-preview-toggle"
                      >
                        {pdfPreviewVisible
                          ? <><EyeOff className="h-3.5 w-3.5 mr-1" />Hide</>
                          : <><Eye className="h-3.5 w-3.5 mr-1" />Show</>}
                      </Button>
                    </CardHeader>
                    {pdfPreviewVisible && (
                      <CardContent>
                        {pdfPreviewLoading || !pdfPreviewUrl ? (
                          <Skeleton className="h-[60vh] sm:h-[700px] w-full" />
                        ) : (
                          <>
                            {/* Inline iframe on tablets/desktop; explicit Open button on mobile (iframes for PDFs are unreliable in iOS Safari / Chrome mobile) */}
                            <iframe
                              src={pdfPreviewUrl}
                              title="Paystub PDF preview"
                              className="hidden sm:block w-full h-[700px] border rounded"
                              data-testid="paystub-preview-iframe"
                            />
                            <div className="sm:hidden flex flex-col items-center gap-3 py-6 px-4 bg-slate-50 rounded border-2 border-dashed border-slate-300">
                              <FileText className="h-10 w-10 text-blue-600" />
                              <p className="text-sm text-slate-600 text-center">Mobile browsers can't render inline PDFs reliably — tap below to open the full preview.</p>
                              <Button
                                onClick={() => window.open(pdfPreviewUrl, '_blank')}
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                data-testid="paystub-preview-open-mobile"
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Open Paystub PDF
                              </Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    )}
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit Gross Pay (manual override) dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="paystub-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-blue-600" />
              Edit Paystub Amount
            </DialogTitle>
            <DialogDescription>
              Manually set the {summary?.override_label === 'Net Payout' ? 'net payout' : 'gross pay'} for this week.
              This will override the calculated amount until you revert it. Useful for off-system adjustments,
              historical corrections, or one-off settlement amounts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Week</Label>
              <div className="font-mono text-sm text-muted-foreground">{fmt(weekStart)} → {fmt(payDayFri)}</div>
            </div>
            <div>
              <Label>{summary?.override_label === 'Net Payout' ? 'Net Payout' : 'Gross Pay'} ($) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={overrideForm.amount}
                onChange={(e) => setOverrideForm({ ...overrideForm, amount: e.target.value })}
                placeholder="0.00"
                data-testid="paystub-override-amount"
                autoFocus
              />
            </div>
            <div>
              <Label>Reason / Notes (optional)</Label>
              <Input
                value={overrideForm.notes}
                onChange={(e) => setOverrideForm({ ...overrideForm, notes: e.target.value })}
                placeholder="e.g. Adjusted for missing load, settlement agreement"
                data-testid="paystub-override-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveOverride}
              disabled={overrideSaving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="paystub-override-save"
            >
              {overrideSaving ? 'Saving…' : 'Save Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add bonus/deduction dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle>{addMode === 'bonus' ? 'Add Bonus' : 'Add Deduction'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="addon-name-input" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder={addMode === 'bonus' ? 'Safety Bonus' : 'Fuel, Insurance'} />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input data-testid="addon-amount-input" type="number" step="0.01" value={addForm.amount} onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })} placeholder="100" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAddOrDeduct} className={addMode === 'bonus' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} data-testid="save-bonus-or-deduction">
              {addMode === 'bonus' ? 'Add Bonus' : 'Add Deduction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inline component that fetches the bonuses for the selected driver/week
function BonusList({ driverId, weekStart, refreshKey = 0, onDelete }) {
  const [bonuses, setBonuses] = useState([]);
  useEffect(() => {
    let cancelled = false;
    driverAddonsApi.getAll(driverId, weekStart).then((r) => {
      if (!cancelled) setBonuses(r.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [driverId, weekStart, refreshKey]);

  if (bonuses.length === 0) {
    return <div className="text-center py-6 text-sm text-muted-foreground">No bonuses this week</div>;
  }
  return (
    <Table>
      <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
      <TableBody>
        {bonuses.map((b) => (
          <TableRow key={b.id}>
            <TableCell className="font-medium">{b.name}</TableCell>
            <TableCell><Badge variant="secondary">{b.addon_type || 'bonus'}</Badge></TableCell>
            <TableCell className="text-right font-mono text-emerald-600">+${b.amount.toLocaleString()}</TableCell>
            <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => onDelete(b.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
